<template>
  <div class="app">
    <cl-sidebar v-model="isClosed">
      <template #company-logo="{ isClosed }">
        <div
          v-if="isRequestPending"
          class="tw-w-[154px] tw-h-[52px] tw-flex tw-justify-center tw-items-center"
        >
          <cl-spinner :size="'small'" class="tw-mr-2" />
        </div>
        <t-logo
          v-else
          @click.prevent="redirectBasedOnRole"
          :logo-src="logoSrc"
          v-show="!isClosed"
        />
      </template>
      <template #dropdown-select="{ isClosed }">
        <t-nav-dropdown
          v-if="
            selectedAccount?.type_name === 'System' ||
            selectedAccount?.type_name === 'MSP' ||
            selectedAccount?.type_name === 'Distributor'
          "
          icon="cl-tabler-icon-users"
          data-test-id="navbar-select-customer"
          :loading="false"
          :is-closed="isClosed"
          :options="userAccountList || []"
          :initial-selected="initialMsp?.uuid"
          data-clarity-mask="true"
          @reset-list="setDropDownList"
          @search="itemBeingSearched"
          @selected="customerSelected"
        />
      </template>
      <template #side-nav="{ isClosed: isSidebarCollapsed }">
        <t-side-nav
          v-if="!loadingSolutions"
          :nav-menu-set-up="customerNavMenuSetUp"
          :is-sidebar-collapsed="isSidebarCollapsed"
          :tier="tier"
        ></t-side-nav>
      </template>
      <template #info-section="{ isClosed }" v-if="isDefaultBranding">
        <img
          v-if="!isClosed"
          alt="CyberSentriq logo"
          :src="cyberSentriqLogoSrc"
        />
      </template>
    </cl-sidebar>
    <div class="app-content">
      <t-upper-nav />
      <main
        class="tw-flex-1 tw-overflow-y-auto tw-bg-primary-grey-25 tw-p-4 max-lg:tw-ml-14"
      >
        <div class="tw-flex tw-flex-col tw-gap-2">
          <read-only-role-banner
            :visible="isReadOnlyRoleVisible"
            level="customer"
          />

          <ms-banner v-if="isBanner && !getAzureAccounts.length"></ms-banner>
        </div>

        <div class="tw-w-full">
          <t-page-header
            :product-links="[]"
            :user-links="userLinks"
            :data-test-id="'header-user-menu'"
          />
          <router-view />
        </div>
      </main>
      <cl-footer :msp-name="getOrganisationBranding.name" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  onBeforeMount,
  computed,
  onMounted,
  watch,
  type Ref,
  type ComputedRef,
} from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { getMediaUrl } from "@/config/runtimeConfig";

import { TIER } from "@/constants";

import msBanner from "@/views/Common/MicrosoftBanner/index.vue";
import TPageHeader from "@/components/TPageHeader";
import TNavDropdown from "@/components/TNavDropdown";
import {
  usePrimaryNavItems,
  useEnabledSolutions,
  useRedirectBasedOnRole,
} from "@/composables";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";

import { NAV_ITEMS_MODE } from "@/constants";
import TSideNav from "@/components/TSideNav";

import { ALL_SERVICES, type AllServicesObject } from "@/utils/services";
import TLogo from "@/components/TLogo/TLogo.vue";
import TUpperNav from "@/components/TUpperNav/TUpperNav.vue";
import ReadOnlyRoleBanner from "@/views/Common/ReadOnlyRoleBanner.vue";
import { isNavItemDisabled } from "@/utils/navItemHelpers";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import type { UserLink } from "@/components/TPageHeader/TPageHeader.types";
import type { LicenceWithSeats } from "@/types/licenceSolutions";
import type { ChildItem, DataItem } from "@/components/TSideNav/types";
import { getAccountShortName } from "@/utils/getAccountShortName";
import { DEFAULT_BRANDING_NAME } from "@/modules/Settings/constants";
import { storeToRefs } from "pinia";
import { useSidebarStore } from "@/store/sidebar";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";

type AuthUser = {
  uuid: string;
  role: string;
  name?: string;
  email?: string;
  twoFactorAuthEnabled?: boolean;
  expired?: number;
  level?: string;
};

type SelectedAccount = {
  type_name: "System" | "MSP" | "Customer" | "Distributor";
  uuid: string;
  name?: string;
};

type SelectedMsp = {
  name: string;
  uuid: string;
};

type SelectedCustomer = {
  name: string;
  uuid: string;
};

type AzureAccount = {
  id: string;
  name?: string;
};

type EnabledSolution = LicenceWithSeats & {
  service: {
    name: string;
    uuid: string;
  };
};

type DropDownAccount = {
  uuid: string;
  name: string;
  organisation_type: string;
};

type DropdownItem = {
  id: string | number;
  name: string;
};

type UserAccount = DropdownItem & {
  uuid: string;
  short: string;
  active: boolean;
  allCustomers: boolean;
  allMsps?: boolean;
  placeholder: string;
};

type OrganisationBranding = {
  name: string;
  logo_dark?: string;
};

const store = useStore();
const router = useRouter();
const route = useRoute();
const redirectBasedOnRole = useRedirectBasedOnRole();

const { enabledSolutions } = useEnabledSolutions();
const { customerGlobalOrganisationId, mspGlobalOrganisationId } =
  useGlobalOrganisationID();
const { navItems } = usePrimaryNavItems();
const { setRequestStatus, isRequestPending } = useRequestStatus();

const customerAdminNavItems: DataItem[] = navItems.get(
  NAV_ITEMS_MODE.CUSTOMER_ADMIN,
);
const loadingSolutions: Ref<boolean> = ref(true);
const isBanner: Ref<boolean> = ref(false);
const initialMsp: Ref<SelectedMsp | null> = ref(null);
const tier = TIER.customer;
const userAccountList: Ref<UserAccount[] | null> = ref(null);
const userLinks: Ref<UserLink[]> = ref([]);
const sidebarStore = useSidebarStore();
const { isClosed } = storeToRefs(sidebarStore);

const getSolutions = async (uuid: string): Promise<void> => {
  await store.dispatch("solutions/getSolutions", {
    uuid,
  });
};

const selectedMsp: ComputedRef<SelectedMsp | null> = computed(
  () => store.getters["authentication/selectedMsp"],
);

const selectedAccount: ComputedRef<SelectedAccount | null> = computed(
  () => store.getters["authentication/selectedAccount"],
);

const authUser: ComputedRef<AuthUser | null> = computed(
  () => store.getters["authentication/authUser"],
);

const selectedCustomer: ComputedRef<SelectedCustomer | null> = computed(
  () => store.getters["authentication/selectedCustomer"],
);

const getAzureAccounts: ComputedRef<AzureAccount[]> = computed(
  () => store.getters["azure/getAzureAccounts"],
);

const dropDownList: ComputedRef<DropDownAccount[]> = computed(
  () => store.getters["dropDown/dropDownList"],
);

const getOrganisationBranding: ComputedRef<OrganisationBranding> = computed(
  () => store.getters["branding/getOrganisationBranding"],
);

const isReadOnlyRoleVisible: ComputedRef<boolean> = computed(() => {
  return authUser.value?.role === "admin-ro";
});

const customerNavMenuSetUp: ComputedRef<Record<string, DataItem[]>> = computed(
  () => {
    const items = customerAdminNavItems
      .map((item: DataItem) => {
        const isSolutionDisabled = ref(false);

        if (item.solutionId) {
          isSolutionDisabled.value = !enabledSolutions.value.some(
            (solution: EnabledSolution) =>
              solution.service.uuid === item.solutionId,
          );
        }

        // If no license then solution should not be visible on Customer level
        // https://gitlab.titanhq.com/titanhq/dev/scrum/team-platfarmers/-/issues/1624
        if (isSolutionDisabled.value) {
          return;
        }

        return {
          ...item,
          disabled: isSolutionDisabled.value,
          children: item.children
            ?.filter(
              (child: ChildItem) =>
                !isNavItemDisabled({
                  navItem: child,
                  enabledSolutions: enabledSolutions.value,
                  parentDisabled: isSolutionDisabled.value,
                  mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                }),
            )
            .map((child: ChildItem) => {
              return {
                ...child,
                children: child.children?.filter(
                  (grandChild: ChildItem) =>
                    !isNavItemDisabled({
                      navItem: grandChild,
                      enabledSolutions: enabledSolutions.value,
                      parentDisabled: false,
                      mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                    }),
                ),
              };
            }),
        };
      })
      .filter(Boolean) as unknown as DataItem[];

    return separatedByGroup(items);
  },
);

const logoSrc: ComputedRef<string> = computed(() => {
  if (getOrganisationBranding.value.logo_dark) {
    return `${getMediaUrl()}/${getOrganisationBranding.value.logo_dark}`;
  }
  return new URL("@/assets/branding/Titan_Logo.svg", import.meta.url).href;
});

const isDefaultBranding = computed<boolean>(() => {
  return !!(
    !getOrganisationBranding.value.logo_dark &&
    getOrganisationBranding.value.name === DEFAULT_BRANDING_NAME
  );
});

const cyberSentriqLogoSrc = new URL(
  "@/assets/branding/powered-by-cybersentriq.svg",
  import.meta.url,
).href;

onMounted(async () => {
  setRequestStatus({ type: PENDING });
  await store.dispatch("branding/getBranding", {
    organisationUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
  });
  setRequestStatus({ type: READY });
});

const redirectToActiveSolution = async () => {
  // Don't redirect if solutions are still loading or empty
  if (loadingSolutions.value || !enabledSolutions.value.length) {
    return;
  }

  let isSolutionDisabled = false;

  isSolutionDisabled = !enabledSolutions.value.some(
    (solution: EnabledSolution) =>
      solution.solution_uuid === route.meta.solutionId,
  );

  if (isSolutionDisabled) {
    // Find next default route name for customer based on the next available solution
    // TODO - remove this when customer dashboard is available and replace "customerUsageRoute" to dashboard one!
    const uuidOrder = ALL_SERVICES.map(
      (service: AllServicesObject) => service.uuid,
    );
    const enabledSolutionsSorted = enabledSolutions.value.sort(
      (a: EnabledSolution, b: EnabledSolution) => {
        return (
          uuidOrder.indexOf(a.solution_uuid) -
          uuidOrder.indexOf(b.solution_uuid)
        );
      },
    );
    const findService = ALL_SERVICES.find(
      (service: AllServicesObject) =>
        service.uuid === enabledSolutionsSorted[0]?.solution_uuid,
    );
    const nextRouteName = findService
      ? findService.customerDefaultRouteName
      : "customerUsageRoute";

    await router.push({ name: nextRouteName });
  }
};

onBeforeMount(async () => {
  if (!customerGlobalOrganisationId.value) {
    return;
  }

  try {
    loadingSolutions.value = true;

    store.dispatch("solutions/resetState");
    await getSolutions(customerGlobalOrganisationId.value);
    await store.dispatch("branding/getBranding", {
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });
  } finally {
    if (route.meta.solutionId) {
      redirectToActiveSolution();
    }

    loadingSolutions.value = false;
    // Call redirect again after solutions are loaded, in case it was skipped earlier
    if (route.meta.solutionId) {
      redirectToActiveSolution();
    }
  }
});

const initializeLayout = async (): Promise<void> => {
  let parentPath = "";
  if (selectedAccount.value?.type_name === "System") {
    parentPath += "/system/msp";
  }
  if (selectedAccount.value?.type_name === "MSP") {
    parentPath += "/msp";
  }
  if (selectedAccount.value?.type_name === "Distributor") {
    parentPath += "/msp";
  }

  await store.dispatch("pageMeta/setParentPath", parentPath);

  if (dropDownList.value) {
    userAccountList.value = cleanAccounts(dropDownList.value);
    if (userAccountList.value[0] && selectedCustomer.value?.name) {
      userAccountList.value[0].name = selectedCustomer.value.name;
    }
  }

  if (selectedCustomer.value?.name) {
    await store.dispatch(
      "pageMeta/setCustomerName",
      selectedCustomer.value.name,
    );
  }
};

const initializeData = async (): Promise<void> => {
  setSupportLinks();
  try {
    await store.dispatch(
      "azure/getAllAzureAccounts",
      selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "",
    );

    if (getAzureAccounts.value.length > 0) {
      await store.dispatch("azure/numberOfSeats", {
        organisation:
          selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "",
        azureID: getAzureAccounts.value[0].id,
      });
    }
  } catch (_e) {
    // swallow errors; handled upstream
  } finally {
    isBanner.value = !getAzureAccounts.value.length;
  }
};

onMounted(async () => {
  await initializeLayout();
  await initializeData();
});

const customerSelected = async (customer: DropdownItem): Promise<void> => {
  const customerData = customer as UserAccount;
  if (customerData.allCustomers) {
    await store.dispatch("authentication/setCustomer", null);
    await router.push(
      buildOrgUrl("msp", selectedMsp.value?.uuid || null, "customers"),
    );
  } else {
    await store.dispatch("authentication/setCustomer", {
      name: customerData.name,
      uuid: customerData.uuid,
    });
    await store.dispatch("pageMeta/setCustomerName", customerData.name);
    await store.dispatch("azure/clearAzureCache");
    await router.push(
      buildOrgUrl("customer", customerData.uuid || null, "email_security"),
    );
    router.go(0);
  }
};

const separatedByGroup = (
  customerArray: DataItem[],
): Record<string, DataItem[]> => {
  const customerByGroup: Record<string, DataItem[]> = {};

  customerArray.forEach((item: DataItem) => {
    if (customerByGroup[item.groupBy]) {
      customerByGroup[item.groupBy].push(item);
    } else {
      customerByGroup[item.groupBy] = [item];
    }
  });
  return customerByGroup;
};

const setSupportLinks = (): void => {
  const id = authUser.value?.uuid;

  userLinks.value = [
    {
      title: "Support", // Using string literal since $t is not available in setup
      link: "https://support.titanhq.com/en/69450-welcome-to-the-titanhq-platform-.html",
      target: "_blank",
      icon: "cl-icon-tabler-lifebuoy",
    },
    {
      title: "Security",
      link: `/profile/security?id=${id}`,
      target: "_blank",
      icon: "cl-icon-tabler-settings",
    },
  ];
};

const setDropDownList = async (): Promise<void> => {
  const params = {
    filter: "",
    results: 20,
  };
  try {
    await store.dispatch("dropDown/resetState");
    await store.dispatch("dropDown/getDropDownList", {
      globalOrganisationID:
        selectedMsp.value?.uuid || selectedAccount.value?.uuid,
      params: params,
    });
  } finally {
    if (dropDownList.value) {
      userAccountList.value = cleanAccounts(dropDownList.value);
    }
  }
};

const itemBeingSearched = async (item: string): Promise<void> => {
  try {
    const params = {
      filter: item,
      results: 25,
    };
    await store.dispatch("dropDown/resetState");
    await store.dispatch("dropDown/getDropDownList", {
      globalOrganisationID:
        selectedMsp.value?.uuid || selectedAccount.value?.uuid,
      params: params,
    });
  } finally {
    // Note: loading property was referenced but not defined in original - might need to be added
  }
};

const cleanAccounts = (accounts: DropDownAccount[]): UserAccount[] => {
  const allAccounts = accounts
    .map((acct: DropDownAccount) => {
      return {
        ...acct,
        id: acct.uuid,
        name: acct.name || "",
        short: getAccountShortName(acct.name || ""),
        active: false,
        allCustomers: false,
        placeholder: "Search Customers",
      };
    })
    .filter((res: DropDownAccount) => res.organisation_type === "Customer")
    .sort((a: UserAccount, b: UserAccount) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : b.name.toLowerCase() > a.name.toLowerCase()
          ? -1
          : 0,
    );

  const otherAccounts: UserAccount[] = [
    {
      id: "all-customers",
      uuid: "all-customers",
      name: "All Customers",
      short: "AM",
      allMsps: false,
      allCustomers: true,
      active: false,
      placeholder: "Search Customers",
    },
  ];

  return [...otherAccounts, ...allAccounts];
};

watch(
  dropDownList,
  () => {
    userAccountList.value = cleanAccounts(dropDownList.value);
  },
  { deep: true, immediate: true },
);

const solutions = [
  "email_security",
  "phishing_simulation",
  "security_awareness_training",
  "email_archiving",
];

watch(
  () => route.path,
  (newPath) => {
    if (
      newPath === "/customer" ||
      solutions.some((solution) => newPath.includes(solution))
    ) {
      redirectToActiveSolution();
    }
  },
  { immediate: true },
);
</script>
